<template>
  <div
    role="alert"
    class="mb-md flex items-center rounded-lg border p-md mobile:items-start"
    :v-bind="$attrs"
    :class="{
      'border-positive-400 bg-positive-100 text-positive-600': type === 'success',
      'border-negative-400 bg-negative-100 text-negative-600': type === 'error',
      'border-warning-400 bg-warning-100 text-warning-600': type === 'warning',
      'border-neutral-400 !bg-white text-neutral-600': type === 'info',
      'border-neutral-400 bg-neutral-100 text-neutral-600': type === 'default',
    }"
  >
    <slot name="icon">
      <span class="sr-only">{{ $t('Info') }}</span>
      <component
        :is="icon.component"
        size="base"
        class="mr-sm mobile:min-w-5"
        :class="{
          'rounded-full border border-negative-600': type === 'error',
        }"
      />
    </slot>

    <p
      v-if="message"
      class="mobile:text-sm"
    >
      {{ $t(message) }}
    </p>
    <slot />

    <slot
      v-if="showClose"
      name="close"
    >
      <SfButton
        variant="tertiary"
        class="-m-1.5 ms-auto inline-flex items-center justify-center"
        :class="{
          '!text-positive-600 hover:bg-positive-200': type === 'success',
          '!text-negative-600 hover:bg-negative-200': type === 'error',
          '!text-warning-600 hover:bg-warning-200': type === 'warning',
          '!text-secondary-600 hover:bg-secondary-200': type === 'info',
          '!text-neutral-600 hover:bg-neutral-200': type === 'default',
        }"
        @click="onClose"
      >
        <span class="sr-only">{{ $t('Close') }}</span>
        <SfIconClose
          size="base"
          :aria-label="$t('Close')"
        />
      </SfButton>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { SfIconCheck, SfIconInfo, SfIconWarning, SfIconClose, SfButton } from '@storefront-ui/vue';

defineComponent({ name: 'AlertComponent' });

interface Props {
  type?: 'success' | 'error' | 'warning' | 'info' | 'default';
  showClose?: boolean;
  displayTime?: number;
  message?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
  showClose: false,
  displayTime: 5000,
});

const emit = defineEmits(['close']);

const icon = computed(() => {
  switch (props.type) {
    case 'success':
      return { component: SfIconCheck };
    case 'error':
      return { component: SfIconClose };
    case 'warning':
      return { component: SfIconWarning };
    case 'info':
    case 'default':
    default:
      return { component: SfIconInfo };
  }
});

setTimeout(() => {
  if (!props.showClose) {
    emit('close');
  }
}, props.displayTime);

const onClose = () => {
  emit('close');
};
</script>
